<script setup lang="ts">
import { computed, onMounted, onUnmounted, shallowRef, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import BottomNav from '@/components/BottomNav.vue'
import How2Buy from '@/svg/index/how2buy.svg'
import How2Sell from '@/svg/index/how2sell.svg'
import Buy from '@/svg/index/buy.svg'
import Sell from '@/svg/index/sell.svg'
import Scan from '@/svg/index/scan.svg'
import QRCode from '@/svg/index/qrcode.svg'
import Transfer from '@/svg/index/transfer.svg'
import Bell from '@/svg/bell.svg'

import Logo from '@/svg/logo.svg'
import Trading from '@/svg/trading.svg'
import TopBanner from '@/assets/imgs/top_banner.png'
import TopBannerLogon from '@/assets/imgs/top_banner_logon.png'
import Fast2Buy from '@/assets/imgs/fast2buy.png'
import USDT2Buy from '@/assets/imgs/usdt2buy.png'
import { useAuthStore } from '@/stores/auth'
import { useAssetsStore } from '@/stores/assets'
import { useSessionStore } from '@/stores/session'
import { useNotifyStore } from '@/stores/notify'
import { formatCurrency, formattCountdown, showConfirm } from '@/utils'
import { statusMap, type OrderStatus } from '@/enums'
import { useServerTimeStore } from '@/stores/serverTime'
import { useCountdown } from '@vueuse/core'
import { chatBus } from '@/stores/chat'
import { DownEvent } from '@/enums/chat'
import CS from '@/components/CS.vue'

const serverTimeStore = useServerTimeStore()
console.log(serverTimeStore.serverTs) // 获取最新的服务器时间戳

const notifyStore = useNotifyStore()
const router = useRouter()
const authStore = useAuthStore()
const assetsStore = useAssetsStore()
const sessionStore = useSessionStore()

const isLoggedIn = computed(() => sessionStore.sessionId)
const userProfile = computed(() => assetsStore.userProfile)
const userAssets = computed(() => assetsStore.userAssets)
const centerUnread = computed(() => notifyStore.centerUnread)
const middleNotifyItem = computed(() => notifyStore.notifyData?.feeds.middle.items?.[0])
const middleFeed = computed(() => notifyStore.notifyData?.feeds.middle.items?.[0]?.feed)
const isSelf = assetsStore.isSelf

// 格式化余额显示
const formatBalance = (balance: string | number | undefined) => {
  if (!balance) return '0.00'
  const num = typeof balance === 'string' ? parseFloat(balance) : balance
  return formatCurrency(num)
}

// 格式化CNY金额
const formatCNY = (amount: string | number | undefined) => {
  if (!amount) return '0.00'
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return formatCurrency(num)
}

// 跳转到登录/注册
const goToLogin = () => {
  router.push('/login')
}

// 跳转到常见问题页面
const goToTutorial = (code: string) => {
  switch (code) {
    // 交易买币
    case 'buy':
      location.href = '/docs'
      break
    // 交易卖币
    case 'sell':
      location.href = '/docs?tab=sell'
      break
    // 常见问题
    case 'questions':
      location.href = '/docs/questions'
      break
    // 实名认证
    case 'kyc':
      location.href = '/docs/kyc'
      break
    // USDT教程
    case 'usdt-tutorial':
      location.href = '/docs/usdt-tutorial'
      break
    // 幸运币商家
    case 'king-coin-merchant':
      location.href = '/docs/king-coin-merchant'
      break
  }
}

const expires_at = computed(() => {
  if (!middleFeed.value) return 0
  return middleFeed.value.expires_at - serverTimeStore.getCurrentServerTime()
})

// 订单倒计时(当 expires_at 变化时，不会自动启动，需要手动监听)
const { remaining, start, pause, reset, resume } = useCountdown(expires_at, {
  onComplete() {
    // 倒计时完成后的回调
  },
  immediate: true,
})

// 监听订单数据变化，自动启动倒计时
watch(expires_at, (newVal) => {
  if (newVal > 0) {
    start(newVal)
  }
})

// 监听 WebSocket 推送的首页中间通知
const unsubscribe = chatBus.on((e, payload) => {
  if (e === DownEvent.NotifyMiddle) {
    console.log('收到首页中间通知推送:', payload)
    // 更新 notifyStore 中的中间通知数据
    notifyStore.updateMiddleNotification(payload)

    // 重新计算过期时间并启动倒计时
    if (payload?.feed) {
      const newExpiresAt = payload.feed.expires_at - serverTimeStore.getCurrentServerTime()
      if (newExpiresAt > 0) {
        start(newExpiresAt)
      }
    }
  }
})

// 加载用户数据
onMounted(async () => {
  if (isLoggedIn.value) {
    try {
      await Promise.all([assetsStore.refreshProfile(), assetsStore.refreshAssets()])
      notifyStore.syncNotifications()
    } catch (error) {
      console.error('Failed to load user data:', error)
    } finally {
      start()
    }
  }
})

// 组件卸载时取消订阅
onUnmounted(() => {
  unsubscribe()
})
</script>

<template>
  <div class="page-root bg-background min-h-dvh">
    <!-- Header -->
    <van-sticky>
      <div class="header bg-white px-4 py-3 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <!-- Logo -->
          <Logo class="size-9" />
          <!-- Title / User Info -->
          <div v-if="!isLoggedIn" @click="goToLogin" class="cursor-pointer">
            <div class="text-base font-medium text-gray-900">登录/注册</div>
          </div>
          <div v-else class="flex flex-col">
            <div class="flex items-center gap-2">
              <span class="text-base font-medium text-gray-900">{{
                userProfile?.nickname || 'ddon125'
              }}</span>
              <span
                v-if="!userProfile?.kyc_verified"
                class="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded"
                >未认证</span
              >
            </div>
            <div class="text-xs text-gray-500">UID: {{ userProfile?.id || '25235213' }}</div>
          </div>
        </div>
        <!-- Icons -->
        <div class="flex items-center gap-4">
          <div class="relative">
            <RouterLink to="/user/notify"
              ><Bell class="size-6 text-gray-700 cursor-pointer"
            /></RouterLink>
            <span
              v-if="isLoggedIn && centerUnread > 0"
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
              >{{ centerUnread }}</span
            >
          </div>
          <CS class="size-6 text-gray-700 cursor-pointer" />
        </div>
      </div>
    </van-sticky>

    <!-- Banner Card -->
    <div class="px-4 py-4">
      <div
        v-if="isLoggedIn"
        class="relative rounded-2xl overflow-hidden"
        :style="{ background: `url(${TopBannerLogon}) no-repeat center / cover` }"
      >
        <img v-if="!isLoggedIn" :src="TopBanner" class="w-full h-auto" alt="banner" />
        <div v-else class="p-6 relative">
          <div class="text-white text-xs">当前总资产</div>
          <div class="text-white text-[32px] font-din-bold">
            {{ formatBalance(userAssets?.free) }}
            <span class="text-sm">{{ userAssets?.currency }}</span>
          </div>
          <div class="text-white text-sm">≈ ¥ {{ formatCNY(userAssets?.cny) }}</div>
        </div>
      </div>

      <div
        v-else
        class="relative rounded-2xl overflow-hidden text-white pl-[22px] py-12"
        :style="{ background: `url(${TopBanner}) no-repeat center / cover` }"
      >
        <h2 class="text-[22px] font-semibold pb-1">欢迎来到幸运钱包</h2>
        <p class="text-xs">权威认证 安全省心</p>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="px-4 py-3">
      <div class="bg-white rounded-2xl px-2 py-1">
        <div class="grid grid-cols-4 gap-6 h-16">
          <RouterLink
            :to="{ path: '/trade' }"
            class="flex flex-col items-center justify-center gap-0.5 cursor-pointer"
          >
            <Buy class="size-8" />
            <span class="text-xs text-text-secondary">买币</span>
          </RouterLink>
          <RouterLink
            :to="{ path: '/trade', query: { tab: 'sell' } }"
            class="flex flex-col items-center justify-center gap-0.5 cursor-pointer"
          >
            <Sell class="size-8" />
            <span class="text-xs text-text-secondary">卖币</span>
          </RouterLink>
          <RouterLink
            :to="{ path: '/transfer' }"
            class="flex flex-col items-center justify-center gap-0.5 cursor-pointer"
          >
            <Transfer class="size-8" />
            <span class="text-xs text-text-secondary">转账</span>
          </RouterLink>
          <RouterLink
            :to="{ path: '/user/payment-qrcode' }"
            class="flex flex-col items-center justify-center gap-0.5 cursor-pointer"
          >
            <QRCode class="size-8" />
            <span class="text-xs text-text-secondary">收款码</span>
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- Feature Cards -->
    <div class="px-4 py-2">
      <div class="grid grid-cols-2 gap-2">
        <!-- Fast Buy Card -->
        <RouterLink
          :to="{ path: '/trade', query: { showFilter: 'true' } }"
          class="bg-primary-light rounded-lg px-2 cursor-pointer"
        >
          <div class="flex items-center gap-2">
            <div class="size-15.5">
              <img :src="Fast2Buy" alt="fast to buy" />
            </div>
            <div class="text-text-primary text-sm font-semibold">
              <div>一键买币</div>
              <div>快速匹配</div>
            </div>
          </div>
        </RouterLink>

        <!-- USDT Buy Card -->
        <RouterLink
          :to="{ path: '/usdt-buy-coin' }"
          class="bg-primary-light rounded-lg px-2 cursor-pointer"
        >
          <div class="flex items-center gap-2">
            <div class="size-15.5">
              <img :src="USDT2Buy" alt="usdt to buy" />
            </div>
            <div class="text-text-primary text-sm font-semibold">
              <div>USDT</div>
              <div>购买幸运币</div>
            </div>
          </div>
        </RouterLink>
      </div>
    </div>

    <RouterLink
      :to="`/order/${middleFeed?.order_no}`"
      class="middle-notify flex items-center mx-4 my-2 px-3 py-2.5 bg-white rounded-xl"
      v-if="middleNotifyItem"
    >
      <Trading class="size-8 text-primary mr-2" />
      <b class="text-sm font-semibold">交易中</b>
      <div class="ml-auto text-right">
        <p class="text-sm text-text-primary">
          {{ isSelf(middleFeed?.buyer_id!) ? '买入' : '卖出' }}数量
          {{ middleFeed?.amount }}
        </p>
        <span class="text-xs text-text-third"
          >{{ statusMap[middleFeed?.order_status as OrderStatus] }}
          <span v-if="remaining > 0">{{ formattCountdown(remaining) }}</span></span
        >
      </div>
      <van-icon name="arrow" size="16" class="text-text-secondary ml-2" />
    </RouterLink>

    <!-- Tutorial Section -->
    <div class="px-4 py-6">
      <div class="text-base font-semibold text-text-primary mb-4">幸运币交易教程</div>
      <div class="grid grid-cols-2 gap-2">
        <!-- How to Buy -->
        <div
          class="bg-content rounded-lg p-4 cursor-pointer bg-no-repeat bg-bottom-right relative"
          @click="goToTutorial('buy')"
        >
          <How2Buy class="size-15 absolute bottom-0 right-0" />
          <div class="text-primary text-sm font-semibold relative">交易买币</div>
          <div class="text-text-secondary text-[10px] relative">How to Buy Crypto</div>
        </div>

        <!-- How to Sell -->
        <div
          class="bg-content rounded-lg p-4 cursor-pointer bg-no-repeat bg-bottom-right relative"
          @click="goToTutorial('sell')"
        >
          <How2Sell class="size-15 absolute bottom-0 right-0" />
          <div class="text-primary text-sm font-semibold relative">挂单卖币</div>
          <div class="text-text-secondary text-[10px] relative">How to Sell Crypto</div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="px-4 pb-6">
      <div class="flex items-center justify-between mb-4">
        <div class="text-base font-semibold text-text-primary">常见交易问题</div>
        <div
          class="text-blue-600 text-sm flex items-center gap-1 cursor-pointer"
          @click="goToTutorial('questions')"
        >
          查看更多
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
      <div class="bg-white rounded-xl overflow-hidden">
        <div
          class="px-4 py-4 border-b border-gray-100 flex items-center justify-between cursor-pointer"
          @click="goToTutorial('kyc')"
        >
          <span class="text-sm text-gray-900">如何实名认证呢?</span>
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
        <div
          class="px-4 py-4 border-b border-gray-100 flex items-center justify-between cursor-pointer"
          @click="goToTutorial('usdt-tutorial')"
        >
          <span class="text-sm text-gray-900">USDT如何充值购买幸运L币呢?</span>
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
        <div
          class="px-4 py-4 border-b border-gray-100 flex items-center justify-between cursor-pointer"
          @click="goToTutorial('king-coin-merchant')"
        >
          <span class="text-sm text-gray-900">什么是L币商家?</span>
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
        <div
          class="px-4 py-4 flex items-center justify-between cursor-pointer"
          @click="goToTutorial('buy')"
        >
          <span class="text-sm text-gray-900">如何使用一键买币?</span>
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </div>

    <BottomNav />
  </div>
</template>

<style scoped>
.page-root {
  padding-bottom: 72px;
}
</style>
